import {
  GOALS_OVERVIEW_PAGE_METRIC_CARDS,
  GOALS_METRIC_CARDS,
  REVIEWS_METRIC_CARDS,
  DISCIPLINARY_ACTIONS_METRIC_CARDS,
  DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS,
} from 'Deer/metricCards/constants';
import { DISCIPLINARY_ACTIONS_STATUS_LABELS } from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import { useSelectLocalStorageToReduxPersistenceData } from 'reducers/localStorageToReduxPersistenceDataSlice';
import { REVIEW_MANAGE_GRID_COLUMN_ID } from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageAGGrid/reviewMangeGridConstants';

const goalsAtRiskFilterTester = (filterData) => {
  const models = filterData?.filterModel?.['table-status']?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes('AT_RISK');
  return x;
};

const goalsTeamMemberNoCheckInFilterTester = (filterData) => {
  const models = filterData?.filterModel?.['checkin-count']?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'number');
  const x = filter?.type === 'equals' && filter?.filter === 0;
  return x;
};

const goalsTeamMemberNoGoalsFilterTester = (filterData) => {
  const models = filterData?.filterModel?.['kr-count']?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'number');
  const x = filter?.type === 'equals' && filter?.filter === 0;
  return x;
};

const goalsTeamMemberMoreThan5GoalsFilterTester = (filterData) => {
  const models = filterData?.filterModel?.['kr-count']?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'number');
  const x = filter?.type === 'greaterThanOrEqual' && filter?.filter === 5;
  return x;
};

const reviewsPendingReviewTester = (filterData, columnId) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes('Not Started') && (filter?.values ?? []).includes('In Progress');
  return x;
};

const reviewsIdentifiedForCalibrationTester = (filterData, columnId) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes('Yes');
  return x;
};

const reviewsPendingCalibrationTester = (filterData, columnId) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes('Pending');
  return x;
};

const disciplinaryActionsTester = (filterData, columnId, status) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes(status);
  return x;
};

const disciplinaryActionsNumberTester = (filterData, columnId, count) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'number');
  const x = filter?.type === 'greaterThanOrEqual' && filter?.filter === count;
  return x;
};

const disciplinaryActionsSetTester = (filterData, columnId, value) => {
  const models = filterData?.filterModel?.[columnId]?.filterModels ?? [];
  const filter = models.find((model) => model?.filterType === 'set');
  const x = (filter?.values ?? []).includes(value);
  return x;
};
const useIsMetricCardFilterActive = ({ cardId, persistenceKey }) => {
  const localhostPersistedFilterData = useSelectLocalStorageToReduxPersistenceData();
  const tablePersistedFilterData = localhostPersistedFilterData[persistenceKey];

  if (!tablePersistedFilterData) {
    return false;
  }

  switch (cardId) {
    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.myData.goalsAtRisk:
    case GOALS_METRIC_CARDS.goalsAtRisk:
      return goalsAtRiskFilterTester(tablePersistedFilterData);

    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.directReports.teamMemberNoCheckIn:
    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.allReports.teamMemberNoCheckIn:
    case GOALS_METRIC_CARDS.teamMemberNoCheckIn:
      return goalsTeamMemberNoCheckInFilterTester(tablePersistedFilterData);

    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.directReports.teamMemberNoGoals:
    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.allReports.teamMemberNoGoals:
    case GOALS_METRIC_CARDS.teamMemberNoGoals:
      return goalsTeamMemberNoGoalsFilterTester(tablePersistedFilterData);

    case GOALS_OVERVIEW_PAGE_METRIC_CARDS.company.teamMemberMoreThan5Goals:
    case GOALS_METRIC_CARDS.teamMemberMoreThan5Goals:
      return goalsTeamMemberMoreThan5GoalsFilterTester(tablePersistedFilterData);

    case REVIEWS_METRIC_CARDS.pendingSelfReviews:
      return reviewsPendingReviewTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.SELF_REVIEW_STATUS);

    case REVIEWS_METRIC_CARDS.pendingPeerReviews:
      return reviewsPendingReviewTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.PEER_REVIEW_STATUS);

    case REVIEWS_METRIC_CARDS.pendingUpwardReviews:
      return reviewsPendingReviewTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.UPWARD_REVIEW_STATUS);

    case REVIEWS_METRIC_CARDS.pendingManagerReviews:
      return reviewsPendingReviewTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER_REVIEW_STATUS);

    case REVIEWS_METRIC_CARDS.pendingDLMReviews:
      return reviewsPendingReviewTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.DLM_REVIEW_STATUS);

    case REVIEWS_METRIC_CARDS.identifiedForCalibration:
      return reviewsIdentifiedForCalibrationTester(
        tablePersistedFilterData,
        REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION
      );

    case REVIEWS_METRIC_CARDS.noRatingChange:
      return false;

    case REVIEWS_METRIC_CARDS.pendingCalibration:
      return reviewsPendingCalibrationTester(tablePersistedFilterData, REVIEW_MANAGE_GRID_COLUMN_ID.CALIBRATION_STATUS);

    case DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingAdminApproval:
      return disciplinaryActionsTester(tablePersistedFilterData, 'STATUS', DISCIPLINARY_ACTIONS_STATUS_LABELS.CREATED);

    case DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingManagerPublish:
      return disciplinaryActionsTester(tablePersistedFilterData, 'STATUS', DISCIPLINARY_ACTIONS_STATUS_LABELS.APPROVED);

    case DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingEmployeeAcknowledgement:
      return disciplinaryActionsTester(
        tablePersistedFilterData,
        'STATUS',
        DISCIPLINARY_ACTIONS_STATUS_LABELS.PUBLISHED_TO_EMPLOYEE
      );

    case DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.employeesWithViolations:
      return disciplinaryActionsNumberTester(tablePersistedFilterData, 'TOTAL_VIOLATIONS', 1);

    case DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.highSeverityViolations:
      return disciplinaryActionsSetTester(tablePersistedFilterData, 'VIOLATIONS_BY_SEVERITY', 'high');

    case DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.greaterThanThreeViolations:
      return disciplinaryActionsNumberTester(tablePersistedFilterData, 'TOTAL_VIOLATIONS', 4);

    case DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.pendingAcknowledgement:
      return disciplinaryActionsNumberTester(tablePersistedFilterData, 'UNACKNOWLEDGED_NOTES', 1);

    default:
      return false;
  }
};

export { useIsMetricCardFilterActive };
